import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../api/apiClient";

/** The browser tells the server the user is still here at most this often. */
export const KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;
/** The warning appears this long before the session would end. */
export const WARN_BEFORE_MS = 5 * 60 * 1000;
/** Used until the server reports its own idle timeout (it replies to the first keepalive). */
export const DEFAULT_IDLE_MS = 30 * 60 * 1000;
/** Tabs share their last activity here, so using one tab keeps the others (and the shared session) alive. */
export const ACTIVITY_STORAGE_KEY = "tc-last-activity";

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "wheel"];
const STORAGE_WRITE_THROTTLE_MS = 5000;

function readSharedActivity() {
  try {
    return Number(window.localStorage.getItem(ACTIVITY_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeSharedActivity(time) {
  try {
    window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(time));
  } catch {
    /* storage unavailable: other tabs just cannot see this one's activity */
  }
}

/**
 * Keeps a signed-in session alive for as long as the person is actually using the app, and ends it after a period of
 * inactivity. The server only slides the session on requests the user makes, so this sends a small keepalive while the
 * user is active (mouse, keyboard, touch, scroll) but not otherwise. When the idle timeout is near it exposes
 * `secondsLeft` for a warning; when it passes it calls `onIdleExpired`.
 *
 * @param {{ enabled: boolean, onIdleExpired: () => void }} options
 * @returns {{ secondsLeft: number | null, stayActive: () => void }}
 */
export default function useSessionActivity({ enabled, onIdleExpired }) {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const lastActivity = useRef(Date.now());
  const lastPing = useRef(0);
  const lastStorageWrite = useRef(0);
  const idleMs = useRef(DEFAULT_IDLE_MS);
  const expired = useRef(false);
  const onExpiredRef = useRef(onIdleExpired);
  onExpiredRef.current = onIdleExpired;

  const ping = useCallback(() => {
    lastPing.current = Date.now();
    return apiClient.post("/api/v1/auth/keepalive")
      .then((response) => {
        const reported = response?.data?.idleTimeoutMs;
        if (reported > 0) idleMs.current = reported;
      })
      // A 401 signs the user out in the API client; any other failure just means we try again next time.
      .catch(() => {});
  }, []);

  const markActive = useCallback(() => {
    const now = Date.now();
    lastActivity.current = now;
    if (now - lastStorageWrite.current >= STORAGE_WRITE_THROTTLE_MS) {
      lastStorageWrite.current = now;
      writeSharedActivity(now);
    }
    setSecondsLeft(null);
    if (now - lastPing.current >= KEEPALIVE_INTERVAL_MS) ping();
  }, [ping]);

  useEffect(() => {
    if (!enabled) return undefined;
    expired.current = false;
    lastActivity.current = Date.now();
    ping();

    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, markActive, { passive: true }));
    const timer = setInterval(() => {
      const idleFor = Date.now() - Math.max(lastActivity.current, readSharedActivity());
      const remaining = idleMs.current - idleFor;
      if (remaining <= 0) {
        if (!expired.current) {
          expired.current = true;
          onExpiredRef.current();
        }
        return;
      }
      setSecondsLeft(remaining <= WARN_BEFORE_MS ? Math.ceil(remaining / 1000) : null);
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, markActive));
      clearInterval(timer);
      setSecondsLeft(null);
    };
  }, [enabled, markActive, ping]);

  return { secondsLeft, stayActive: markActive };
}
